import { useEffect, useState } from "react";
import { supabase } from "../../../../lib/supabase";
import { useAuth } from "../../../../hooks/useAuth";
import DashboardLayout from "../../../../components/feature/DashboardLayout";

const navItems = [
  { label: "Dashboard", path: "/dashboard/admin", icon: "ri-dashboard-line" },
  { label: "Clients", path: "/dashboard/admin/clients", icon: "ri-user-line" },
  { label: "Caregivers", path: "/dashboard/admin/caregivers", icon: "ri-user-heart-line" },
  { label: "Appointments", path: "/dashboard/admin/appointments", icon: "ri-calendar-event-line" },
  { label: "Care Plans", path: "/dashboard/admin/care-plans", icon: "ri-file-list-3-line" },
  { label: "Settings", path: "/dashboard/admin/settings", icon: "ri-settings-3-line" },
];

export default function AdminSettings() {
  const { user } = useAuth();
  const [fullName, setFullName] = useState("");
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");
  const [pwdMsg, setPwdMsg] = useState("");
  const [pwdLoading, setPwdLoading] = useState(false);
  const [currentPwd, setCurrentPwd] = useState("");
  const [newPwd, setNewPwd] = useState("");
  const [confirmPwd, setConfirmPwd] = useState("");

  useEffect(() => {
    if (!user) return;
    const load = async () => {
      const { data } = await supabase
        .from("profiles")
        .select("full_name,phone,address,created_at")
        .eq("id", user.id)
        .maybeSingle();
      if (data) {
        setFullName(data.full_name || "");
        setPhone(data.phone || "");
        setAddress(data.address || "");
      }
      setLoading(false);
    };
    load();
  }, [user]);

  const handleProfileSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;
    setSaving(true);
    setMsg("");
    const { error } = await supabase
      .from("profiles")
      .update({ full_name: fullName, phone, address })
      .eq("id", user.id);
    if (error) {
      setMsg("Failed to save profile changes.");
    } else {
      setMsg("Profile updated successfully.");
    }
    setSaving(false);
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setPwdMsg("");
    if (newPwd !== confirmPwd) {
      setPwdMsg("Passwords do not match.");
      return;
    }
    if (newPwd.length < 6) {
      setPwdMsg("Password must be at least 6 characters.");
      return;
    }
    setPwdLoading(true);
    const { error } = await supabase.auth.updateUser({ password: newPwd });
    if (error) {
      setPwdMsg(error.message);
    } else {
      setPwdMsg("Password updated successfully.");
      setCurrentPwd("");
      setNewPwd("");
      setConfirmPwd("");
    }
    setPwdLoading(false);
  };

  if (loading) {
    return (
      <DashboardLayout navItems={navItems} title="Admin Portal" subtitle="Settings">
        <div className="flex items-center gap-3 py-12">
          <div className="w-5 h-5 border-2 border-brand-green-600 border-t-transparent rounded-full animate-spin" />
          <span className="text-sm text-gray-500">Loading settings...</span>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout navItems={navItems} title="Admin Portal" subtitle="Settings">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Profile Card */}
        <div className="bg-white rounded-2xl p-6 border border-gray-100">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-full bg-brand-green-100 flex items-center justify-center">
              <div className="w-6 h-6 flex items-center justify-center">
                <i className="ri-user-line text-brand-green-600 text-lg" />
              </div>
            </div>
            <div>
              <h2 className="text-base font-semibold text-gray-900">Admin Profile</h2>
              <p className="text-xs text-gray-500">Update your name, phone, and address</p>
            </div>
          </div>

          {msg && (
            <div
              className={`rounded-lg p-3 border mb-5 ${
                msg.includes("success")
                  ? "bg-brand-green-50 border-brand-green-100 text-brand-green-800"
                  : "bg-red-50 border-red-100 text-red-700"
              }`}
            >
              <p className="text-sm text-center">{msg}</p>
            </div>
          )}

          <form onSubmit={handleProfileSave} className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1.5">
                Full Name
              </label>
              <input
                type="text"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                className="w-full px-3 py-2.5 text-sm rounded-lg border border-gray-200 focus:border-brand-green-400 focus:ring-2 focus:ring-brand-green-100 outline-none"
                placeholder="Your full name"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1.5">
                Phone
              </label>
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className="w-full px-3 py-2.5 text-sm rounded-lg border border-gray-200 focus:border-brand-green-400 focus:ring-2 focus:ring-brand-green-100 outline-none"
                placeholder="773-xxx-xxxx"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1.5">
                Address
              </label>
              <textarea
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                rows={2}
                maxLength={500}
                className="w-full px-3 py-2.5 text-sm rounded-lg border border-gray-200 focus:border-brand-green-400 focus:ring-2 focus:ring-brand-green-100 outline-none resize-none"
                placeholder="Office or home address..."
              />
            </div>
            <div className="pt-2">
              <button
                type="submit"
                disabled={saving}
                className="w-full px-4 py-2.5 text-sm font-semibold text-white bg-brand-green-600 rounded-lg hover:bg-brand-green-700 disabled:bg-brand-green-400 transition-all whitespace-nowrap"
              >
                {saving ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </form>
        </div>

        {/* Account Info + Password */}
        <div className="space-y-6">
          {/* Account Info */}
          <div className="bg-white rounded-2xl p-6 border border-gray-100">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-12 h-12 rounded-full bg-blue-50 flex items-center justify-center">
                <div className="w-6 h-6 flex items-center justify-center">
                  <i className="ri-shield-user-line text-blue-600 text-lg" />
                </div>
              </div>
              <div>
                <h2 className="text-base font-semibold text-gray-900">Account Info</h2>
                <p className="text-xs text-gray-500">Read-only account details</p>
              </div>
            </div>
            <div className="space-y-3">
              <div className="flex items-center justify-between py-2 border-b border-gray-100">
                <span className="text-sm text-gray-500">Email</span>
                <span className="text-sm font-medium text-gray-900">{user?.email}</span>
              </div>
              <div className="flex items-center justify-between py-2 border-b border-gray-100">
                <span className="text-sm text-gray-500">Role</span>
                <span className="text-sm font-medium text-gray-900 capitalize">{user?.role}</span>
              </div>
              <div className="flex items-center justify-between py-2 border-b border-gray-100">
                <span className="text-sm text-gray-500">User ID</span>
                <span className="text-sm font-medium text-gray-900 font-mono">
                  {user?.id.slice(0, 12)}...
                </span>
              </div>
            </div>
          </div>

          {/* Change Password */}
          <div className="bg-white rounded-2xl p-6 border border-gray-100">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-12 h-12 rounded-full bg-amber-50 flex items-center justify-center">
                <div className="w-6 h-6 flex items-center justify-center">
                  <i className="ri-lock-password-line text-amber-600 text-lg" />
                </div>
              </div>
              <div>
                <h2 className="text-base font-semibold text-gray-900">Change Password</h2>
                <p className="text-xs text-gray-500">Set a new admin password</p>
              </div>
            </div>

            {pwdMsg && (
              <div
                className={`rounded-lg p-3 border mb-5 ${
                  pwdMsg.includes("success")
                    ? "bg-brand-green-50 border-brand-green-100 text-brand-green-800"
                    : "bg-red-50 border-red-100 text-red-700"
                }`}
              >
                <p className="text-sm text-center">{pwdMsg}</p>
              </div>
            )}

            <form onSubmit={handlePasswordChange} className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1.5">
                  New Password
                </label>
                <input
                  type="password"
                  value={newPwd}
                  onChange={(e) => setNewPwd(e.target.value)}
                  required
                  minLength={6}
                  className="w-full px-3 py-2.5 text-sm rounded-lg border border-gray-200 focus:border-brand-green-400 focus:ring-2 focus:ring-brand-green-100 outline-none"
                  placeholder="Min 6 characters"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1.5">
                  Confirm New Password
                </label>
                <input
                  type="password"
                  value={confirmPwd}
                  onChange={(e) => setConfirmPwd(e.target.value)}
                  required
                  className="w-full px-3 py-2.5 text-sm rounded-lg border border-gray-200 focus:border-brand-green-400 focus:ring-2 focus:ring-brand-green-100 outline-none"
                  placeholder="Re-enter password"
                />
              </div>
              <div className="pt-2">
                <button
                  type="submit"
                  disabled={pwdLoading}
                  className="w-full px-4 py-2.5 text-sm font-semibold text-white bg-amber-600 rounded-lg hover:bg-amber-700 disabled:bg-amber-400 transition-all whitespace-nowrap"
                >
                  {pwdLoading ? "Updating..." : "Update Password"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}